// SEO utility - LiberaCure international pharmacy

import type { SupabaseProduct } from '../hooks/useSupabaseProducts';
import type { FAQPage } from '../data/policyContent';
import type { ComparisonPage } from '../data/comparisonContent';
import { buildBreadcrumbSchema, BreadcrumbItem } from './breadcrumb';
import { formatCategoryLabel } from '../types';
import { stripMarkdown } from './markdown-strip';

export interface SEOData {
  title: string;
  description: string;
  keywords: string;
  openGraph: {
    title: string;
    description: string;
    image: string;
    url: string;
    type: string;
  };
  twitter: {
    card: string;
    title: string;
    description: string;
    image: string;
  };
  jsonLD?: any[];
}

// Site configuration
export const SITE_CONFIG = {
  name: "LiberaCure",
  description: "Affordable generic medications from trusted Indian manufacturers. Sildenafil, Tadalafil, Finasteride, and more.",
  url: "https://www.liberacure.com",
  // Absolute URL — Slack/Telegram/Discord/Twitter resolve og:image against the
  // sharer's domain, not ours, so a relative path 404s on every social card.
  image: "https://www.liberacure.com/images/og-image.jpg",
  keywords: [
    "generic medications",
    "Cenforce",
    "Vidalista",
    "Finpecia",
    "Sildenafil",
    "Tadalafil",
    "affordable pharmacy",
    "Indian generics",
    "Minoxidil",
    "Dutasteride"
  ]
};

// Home page SEO
export const getHomeSEO = (): SEOData => ({
  title: "LiberaCure | Affordable Generic Medications - Trusted Indian Pharmacy",
  description: "Shop affordable generic medications from FDA-approved Indian manufacturers. Cenforce, Vidalista, Finpecia, and more delivered worldwide with discreet shipping.",
  keywords: SITE_CONFIG.keywords.join(", "),
  openGraph: {
    title: "LiberaCure - Affordable Generic Medications",
    description: "Trusted source for affordable generic medications from leading Indian pharmaceutical manufacturers.",
    image: SITE_CONFIG.image,
    url: SITE_CONFIG.url,
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "LiberaCure - Affordable Generic Medications",
    description: "Trusted source for affordable generic medications worldwide.",
    image: SITE_CONFIG.image
  },
  jsonLD: [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "LiberaCure",
      "description": SITE_CONFIG.description,
      "url": SITE_CONFIG.url,
      "logo": `${SITE_CONFIG.url}/images/logo.png`,
      "sameAs": [],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": "English"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "LiberaCure",
      "url": SITE_CONFIG.url,
      "description": SITE_CONFIG.description,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_CONFIG.url}/shop?search={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    }
  ]
});

// /honestly page SEO — radical transparency trust page
export const getHonestlySEO = (): SEOData => ({
  title: "Honestly — Why We Look Like a Scam (and Why We're Not) | LiberaCure",
  description: "New site. Crypto only. You'd be right to be skeptical. Here's why we think LiberaCure is worth five minutes of your attention.",
  keywords: "honest pharmacy, transparent dropshipping, new pharmacy trust, crypto pharmacy, LiberaCure",
  openGraph: {
    title: "Honestly — LiberaCure",
    description: "Yes, this probably looks like a scam. Here's why it isn't.",
    image: SITE_CONFIG.image,
    url: `${SITE_CONFIG.url}/honestly`,
    type: "article"
  },
  twitter: {
    card: "summary_large_image",
    title: "Honestly — LiberaCure",
    description: "Yes, this probably looks like a scam. Here's why it isn't.",
    image: SITE_CONFIG.image
  },
  jsonLD: [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Honestly — LiberaCure",
      "description": "Radical transparency: why LiberaCure looks sketchy on first impression and why the structure of our business makes it materially safer than it looks.",
      "url": `${SITE_CONFIG.url}/honestly`
    }
  ]
});

// /mission page SEO — brand-purpose AboutPage
export const getMissionSEO = (): SEOData => {
  const url = `${SITE_CONFIG.url}/mission`;
  const title = 'Our Mission — LiberaCure';
  const description = 'LiberaCure exists to make affordable, high-quality generic medication accessible across borders. We route orders to licensed Indian generic pharmacies under personal-import frameworks — transparently, with no markup theatre.';
  return {
    title,
    description,
    keywords: 'LiberaCure mission, generic medications mission, affordable medication access, personal import pharmacy mission',
    openGraph: {
      title,
      description,
      image: SITE_CONFIG.image,
      url,
      type: 'article',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      image: SITE_CONFIG.image,
    },
    jsonLD: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: title,
        description,
        url,
        publisher: { '@type': 'Organization', name: 'LiberaCure', url: SITE_CONFIG.url },
      },
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Mission', url: '/mission' },
      ]),
    ],
  };
};

// /guides/* SEO — pillar + cluster editorial hub
//
// Three helpers, three Schema.org types:
//   - Hub        →  CollectionPage (lists categories + latest)
//   - Category   →  CollectionPage (lists articles in one category)
//   - Article    →  Article (full schema with headline, body excerpt, datePublished)
//
// All three include BreadcrumbList for the Guides → Category → Article nav.

const GUIDES_HUB_DESCRIPTION =
  'Plain-language guides on ED, hair loss, GLP-1 weight management, and skin health. Written by people who actually buy these medications and want you to understand the markup before you do.';

export const getGuidesHubSEO = (): SEOData => {
  const url = `${SITE_CONFIG.url}/guides`;
  const title = 'Guides — LiberaCure';
  return {
    title: 'LiberaCure Guides — ED, Hair Loss, GLP-1, Skin (Without the Markup)',
    description: GUIDES_HUB_DESCRIPTION,
    keywords: 'sildenafil guide, finasteride guide, semaglutide guide, isotretinoin guide, telehealth markup, generic medications education, LiberaCure guides',
    openGraph: {
      title,
      description: GUIDES_HUB_DESCRIPTION,
      image: SITE_CONFIG.image,
      url,
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description: GUIDES_HUB_DESCRIPTION,
      image: SITE_CONFIG.image,
    },
    jsonLD: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description: GUIDES_HUB_DESCRIPTION,
        url,
        publisher: { '@type': 'Organization', name: 'LiberaCure', url: SITE_CONFIG.url },
      },
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Guides', url: '/guides' },
      ]),
    ],
  };
};

export const getGuidesCategorySEO = (
  categoryId: string,
  categoryLabel: string,
  tagline: string,
  articleCount: number
): SEOData => {
  const url = `${SITE_CONFIG.url}/guides/${categoryId}`;
  const title = `${categoryLabel} Guides — LiberaCure`;
  const description = `${articleCount} plain-language guides on ${categoryLabel.toLowerCase()}. ${tagline}`;
  return {
    title,
    description,
    keywords: `${categoryLabel.toLowerCase()} guides, ${categoryId.replace('-', ' ')}, generic medications, LiberaCure`,
    openGraph: {
      title,
      description,
      image: SITE_CONFIG.image,
      url,
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      image: SITE_CONFIG.image,
    },
    jsonLD: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url,
        publisher: { '@type': 'Organization', name: 'LiberaCure', url: SITE_CONFIG.url },
      },
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Guides', url: '/guides' },
        { name: categoryLabel, url: `/guides/${categoryId}` },
      ]),
    ],
  };
};

export const getGuideArticleSEO = (
  categoryId: string,
  categoryLabel: string,
  slug: string,
  title: string,
  excerpt: string
): SEOData => {
  const url = `${SITE_CONFIG.url}/guides/${categoryId}/${slug}`;
  return {
    title: `${title} | LiberaCure Guides`,
    description: excerpt,
    keywords: `${categoryLabel.toLowerCase()}, generic medication, ${slug.replace(/-/g, ' ')}, LiberaCure`,
    openGraph: {
      title,
      description: excerpt,
      image: SITE_CONFIG.image,
      url,
      type: 'article',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description: excerpt,
      image: SITE_CONFIG.image,
    },
    jsonLD: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: excerpt,
        image: SITE_CONFIG.image,
        url,
        author: { '@type': 'Organization', name: 'LiberaCure Editorial', url: SITE_CONFIG.url },
        publisher: {
          '@type': 'Organization',
          name: 'LiberaCure',
          url: SITE_CONFIG.url,
          logo: { '@type': 'ImageObject', url: `${SITE_CONFIG.url}/images/logo.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      },
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Guides', url: '/guides' },
        { name: categoryLabel, url: `/guides/${categoryId}` },
        { name: title, url: `/guides/${categoryId}/${slug}` },
      ]),
    ],
  };
};

// Product SEO keyword mapping
const PRODUCT_SEO_MAP: { [key: string]: { keywords: string[], category: string } } = {
  // Sexual health
  'cenforce': { keywords: ['Cenforce', 'Sildenafil', 'generic Viagra'], category: 'ED Treatment' },
  'suhagra': { keywords: ['Suhagra', 'Sildenafil'], category: 'ED Treatment' },
  'vidalista': { keywords: ['Vidalista', 'Tadalafil', 'generic Cialis'], category: 'ED Treatment' },
  'tastylia': { keywords: ['Tastylia', 'Tadalafil'], category: 'ED Treatment' },

  // Hair loss
  'finpecia': { keywords: ['Finpecia', 'Finasteride', 'hair loss treatment'], category: 'Hair Loss Treatment' },
  'dutas': { keywords: ['Dutas', 'Dutasteride'], category: 'Hair Loss Treatment' },
  'minoxidil': { keywords: ['Minoxidil', 'topical hair loss treatment'], category: 'Hair Loss Treatment' },

  // Weight management
  'wegovy': { keywords: ['Wegovy', 'GLP-1', 'Semaglutide', 'Weight Loss Injection'], category: 'Weight Management' },
  'rybelsus': { keywords: ['Rybelsus', 'GLP-1', 'Semaglutide', 'Oral'], category: 'Weight Management' },
  'xenical': { keywords: ['Xenical', 'Orlistat'], category: 'Weight Management' },

  // Skin care
  'accutane': { keywords: ['Accutane generic', 'Isotretinoin'], category: 'Acne Treatment' }
};

// Resolve a relative or already-absolute image URL to absolute (for JSON-LD).
const absoluteUrl = (path?: string | null): string => {
  if (!path) return `${SITE_CONFIG.url}${SITE_CONFIG.image}`;
  if (path.startsWith('http')) return path;
  return `${SITE_CONFIG.url}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Slugify a category name into the URL slug used by /shop/:category routes.
// Mirrors the convention used elsewhere in the app (lowercase, hyphens, ampersands stripped).
const categorySlug = (name?: string | null): string => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Compute aggregate price range across all variants/tiers for AggregateOffer schema.
const computePriceRange = (product: SupabaseProduct): { low: number; high: number; offerCount: number } => {
  const allPrices: number[] = [];
  let offerCount = 0;
  for (const variant of product.variants || []) {
    for (const p of variant.prices || []) {
      if (p.sale_price > 0) {
        allPrices.push(p.sale_price);
        offerCount += 1;
      }
    }
  }
  if (allPrices.length === 0) {
    const fallback = product.final_price || product.price || 0;
    return { low: fallback, high: fallback, offerCount: 0 };
  }
  return {
    low: Math.min(...allPrices),
    high: Math.max(...allPrices),
    offerCount,
  };
};

// Product page SEO — generates Product + BreadcrumbList + (optional) FAQPage JSON-LD
// from a SupabaseProduct. Pass productFaqs to append per-product Q&A schema.
export const getProductSEO = (
  product: SupabaseProduct,
  productFaqs?: { question: string; answer: string }[],
): SEOData => {
  const productId = product.id;
  const productName = product.name || 'Medication';
  const description = product.description || '';

  const productKey = Object.keys(PRODUCT_SEO_MAP).find(key =>
    productId.toLowerCase().includes(key) || productName.toLowerCase().includes(key)
  );

  const seoCategoryData = productKey ? PRODUCT_SEO_MAP[productKey] : {
    keywords: [productName],
    category: product.category_name || 'Medication',
  };

  const optimizedTitle = `${productName} | ${seoCategoryData.category} | LiberaCure`;
  const optimizedDescription = description ||
    `Buy ${productName} online at affordable prices. ${seoCategoryData.keywords.join(', ')} from licensed Indian manufacturers with worldwide shipping.`;
  const cleanDescription = stripMarkdown(optimizedDescription);
  const truncatedDesc = cleanDescription.length > 500 ? cleanDescription.slice(0, 497) + '...' : cleanDescription;

  const priceRange = computePriceRange(product);
  const inStock = (product.stock ?? 1) > 0;
  const productUrl = `${SITE_CONFIG.url}/product/${productId}`;
  const imageUrl = absoluteUrl(product.image);

  const breadcrumbTrail: BreadcrumbItem[] = [
    { name: 'Home', url: '/' },
    { name: 'Shop', url: '/shop' },
  ];
  if (product.category_name) {
    const slug = product.categories?.[0] || categorySlug(product.category_name);
    breadcrumbTrail.push({ name: formatCategoryLabel(slug), url: `/shop/${slug}` });
  }
  breadcrumbTrail.push({ name: productName, url: `/product/${productId}` });

  const productSchema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    image: [imageUrl],
    description: truncatedDesc,
    sku: productId,
    category: seoCategoryData.category,
    url: productUrl,
  };
  if (product.manufacturer) {
    productSchema.brand = { '@type': 'Brand', name: product.manufacturer };
    productSchema.manufacturer = { '@type': 'Organization', name: product.manufacturer };
  }
  if (priceRange.offerCount > 0) {
    productSchema.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: priceRange.low,
      highPrice: priceRange.high,
      offerCount: priceRange.offerCount,
      availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      url: productUrl,
      seller: { '@type': 'Organization', name: 'LiberaCure' },
    };
  }

  const jsonLD: any[] = [productSchema, buildBreadcrumbSchema(breadcrumbTrail)];

  if (productFaqs && productFaqs.length > 0) {
    jsonLD.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: productFaqs.map(q => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: q.answer },
      })),
    });
  }

  return {
    title: optimizedTitle,
    description: truncatedDesc,
    keywords: [...seoCategoryData.keywords, 'generic medication', 'affordable pharmacy', 'Indian generics'].join(', '),
    openGraph: {
      title: optimizedTitle,
      description: truncatedDesc,
      image: imageUrl,
      url: productUrl,
      type: 'product',
    },
    twitter: {
      card: 'summary_large_image',
      title: optimizedTitle,
      description: truncatedDesc,
      image: imageUrl,
    },
    jsonLD,
  };
};

// Category page SEO — adds ItemList JSON-LD when products are passed.
export const getCategorySEO = (category: string, products?: SupabaseProduct[]): SEOData => {
  const categoryMap: { [key: string]: { title: string, description: string, keywords: string[] } } = {
    'sexual-health': {
      title: 'ED Treatment | Cenforce, Vidalista, Tadalafil | LiberaCure',
      description: 'Shop affordable ED treatments including Cenforce, Vidalista, and Suhagra. FDA-approved generic alternatives delivered discreetly worldwide.',
      keywords: ['ED treatment', 'Cenforce', 'Vidalista', 'Tadalafil', 'Sildenafil']
    },
    'hair-loss': {
      title: 'Hair Loss Treatment | Finpecia, Dutas, Minoxidil | LiberaCure',
      description: 'Effective hair loss treatments including Finpecia, Dutas, and Minoxidil. Clinically proven generics at affordable prices.',
      keywords: ['Finpecia', 'hair loss treatment', 'Dutas', 'Minoxidil', 'Finasteride']
    },
    'weight-management': {
      title: 'Weight Management | Rybelsus, Xenical | LiberaCure',
      description: 'Weight management medications including Rybelsus and Xenical. GLP-1 and fat absorption inhibitors for healthy weight management.',
      keywords: ['Rybelsus', 'Xenical', 'GLP-1 weight loss', 'weight management medication']
    }
  };

  const seoData = categoryMap[category] || {
    title: `${formatCategoryLabel(category)} | LiberaCure`,
    description: `Shop ${formatCategoryLabel(category)} medications at affordable prices from LiberaCure.`,
    keywords: [category, 'generic medication', 'affordable']
  };

  const categoryUrl = `${SITE_CONFIG.url}/shop/${category}`;

  const jsonLD: any[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: seoData.title,
      description: seoData.description,
      url: categoryUrl,
    },
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Shop', url: '/shop' },
      { name: seoData.title.split(' |')[0], url: `/shop/${category}` },
    ]),
  ];

  if (products && products.length > 0) {
    jsonLD.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: seoData.title.split(' |')[0],
      numberOfItems: products.length,
      itemListElement: products.slice(0, 30).map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_CONFIG.url}/product/${p.id}`,
        name: p.name || 'Medication',
      })),
    });
  }

  return {
    title: seoData.title,
    description: seoData.description,
    keywords: [...seoData.keywords, 'generic medication', 'affordable pharmacy', 'Indian generics'].join(', '),
    openGraph: {
      title: seoData.title,
      description: seoData.description,
      image: SITE_CONFIG.image,
      url: categoryUrl,
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title: seoData.title,
      description: seoData.description,
      image: SITE_CONFIG.image,
    },
    jsonLD,
  };
};

// FAQ page SEO — folds the FAQ_PAGE policy data into a Schema.org FAQPage.
export const getFAQPageSEO = (faqPage: FAQPage): SEOData => {
  const faqUrl = `${SITE_CONFIG.url}/policy/faq`;
  const desc = (faqPage.intro || '').slice(0, 160) ||
    'Common questions about LiberaCure orders, payment, shipping, returns, and personal-import handling.';
  return {
    title: `${faqPage.title} | LiberaCure`,
    description: desc,
    keywords: 'faq, liberacure questions, generic medication faq, crypto pharmacy faq, personal import',
    openGraph: {
      title: faqPage.title,
      description: desc,
      image: SITE_CONFIG.image,
      url: faqUrl,
      type: 'website',
    },
    twitter: {
      card: 'summary',
      title: faqPage.title,
      description: desc,
      image: SITE_CONFIG.image,
    },
    jsonLD: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqPage.groups.flatMap(g => g.entries.map(e => ({
          '@type': 'Question',
          name: e.question,
          acceptedAnswer: { '@type': 'Answer', text: e.answer },
        }))),
      },
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Policies', url: '/policy/faq' },
        { name: faqPage.title, url: '/policy/faq' },
      ]),
    ],
  };
};

// Legal disclaimer
export const LEGAL_DISCLAIMER = {
  title: "Medical Disclaimer & Legal Notice",
  content: `
This website provides purchasing agent services for generic medications and does not directly sell medications or provide medical services.

**Important Notice:**
- The information on this site is not a substitute for professional medical advice, diagnosis, or treatment.
- Always consult with a qualified healthcare provider before starting any medication.
- All medications carry potential side effects - please review product information carefully.
- This service is intended for personal use only and is subject to applicable laws and regulations in your jurisdiction.
- Customers are responsible for compliance with their local import regulations.
  `
};

// Comparison page SEO — Article + BreadcrumbList + (optional) FAQPage JSON-LD.
export const getComparisonPageSEO = (comparison: ComparisonPage): SEOData => {
  const url = `${SITE_CONFIG.url}/compare/${comparison.slug}`;
  const desc = comparison.intro.length > 160 ? comparison.intro.slice(0, 157) + '...' : comparison.intro;

  const jsonLD: any[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: comparison.title,
      description: comparison.intro,
      author: { '@type': 'Organization', name: 'LiberaCure Editorial Team' },
      publisher: {
        '@type': 'Organization',
        name: 'LiberaCure',
        logo: { '@type': 'ImageObject', url: `${SITE_CONFIG.url}/images/logo.png` },
      },
      datePublished: comparison.lastUpdated,
      dateModified: comparison.lastUpdated,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    },
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Compare', url: '/' },
      { name: comparison.title, url: `/compare/${comparison.slug}` },
    ]),
  ];

  if (comparison.faq && comparison.faq.length > 0) {
    jsonLD.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: comparison.faq.map(q => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: q.answer },
      })),
    });
  }

  return {
    title: `${comparison.title} | LiberaCure`,
    description: desc,
    keywords: `${comparison.slug.replace(/-/g, ' ')}, generic vs brand, Indian generics, LiberaCure comparison`,
    openGraph: {
      title: comparison.title,
      description: desc,
      image: SITE_CONFIG.image,
      url,
      type: 'article',
    },
    twitter: {
      card: 'summary_large_image',
      title: comparison.title,
      description: desc,
      image: SITE_CONFIG.image,
    },
    jsonLD,
  };
};
